/**
 * wallet.ts — the credential form. Pure rendering + validation; no fetch calls.
 *
 * `renderWallet` BELOW IS NOT RENDERED BY THE APP (verified 2026-08-17: zero non-test references).
 * The live wallet panel is `profile.renderWalletPanel`, which replaced it when the wallet moved out
 * of the trade panel into the profile modal, and the live stop-loss checkbox is
 * `profile.renderAlertsPanel`. This function and its tests are kept as the plain-language /
 * no-key-in-the-DOM reference, but do NOT read them as evidence that a control is reachable — a
 * card was written on exactly that mistake. `validate` IS live; `main.ts` imports it.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Before it, a user had NO way to supply Hyperliquid credentials. `exec_tools` read them
 * from `exec_tools/.env` — a file that exists only on the EC2 host and can only be edited
 * over an SSM port-forward. The account panel therefore showed:
 *
 *     panel unavailable — no wallet address — set HYPERLIQUID_ACCOUNT_ADDRESS ... in env
 *
 * which is an error telling the user to set something they had no way to set. This form is
 * the missing half.
 *
 * WHY IT IS SEPARATE FROM main.ts
 * -------------------------------
 * Same reason as `auth.ts` and `aichat.ts`: `main.ts` is 3,000+ lines of chart wiring, and
 * pure functions in their own file can be unit-tested without a DOM or a server. The
 * validation here is the part worth testing — it is what stops a malformed key reaching
 * the money path.
 *
 * THE KEY IS WRITE-ONLY, BY CONSTRUCTION
 * --------------------------------------
 * `GET /api/account/credentials` never returns the private key, so this form cannot
 * pre-fill it and does not try. It shows the ADDRESS (public on-chain data) and a
 * `key_present` boolean. Re-saving is how you rotate; there is no "reveal".
 */

/** What the server reports about the stored credentials. Never includes the key. */
export interface CredentialState {
  configured: boolean;
  address: string | null;
  testnet?: boolean;
  key_present?: boolean;
  trading_enabled?: boolean;
  /**
   * Is this wallet subscribed to the daily stop-loss advisor?
   *
   * ON THE SAME PAYLOAD AS THE WALLET because it is an attribute of the same users-table row and
   * cannot be set without an address (`sl_monitor.set_enabled` writes with
   * `attribute_exists(address)`). Optional and ABSENT MEANS OFF, matching the storage default: a
   * deployment whose API predates this field must not render a subscription nobody asked for.
   */
  sl_monitor_enabled?: boolean;
  error?: string;
}

export interface CredentialInput {
  address: string;
  privateKey: string;
  testnet: boolean;
}

/**
 * Validate before sending. Returns null when valid, else a human-readable reason.
 *
 * DUPLICATED ON THE SERVER ON PURPOSE — and this is the one place duplication is right.
 * The server validates because it must never store a malformed key; the client validates
 * so the user gets an instant, specific message instead of a round-trip and a 422. These
 * are not two sources of truth about a RULE, they are the same rule enforced at two trust
 * boundaries. The server's copy is authoritative; this one is a courtesy.
 */
export function validate(input: CredentialInput): string | null {
  const address = input.address.trim();
  const key = input.privateKey.trim();

  if (!address) return 'account address is required';
  if (!address.startsWith('0x')) return 'address must start with 0x';
  if (address.length !== 42) {
    return `address must be 42 characters (0x + 40 hex), got ${address.length}`;
  }
  if (!/^0x[0-9a-fA-F]{40}$/.test(address)) return 'address contains non-hex characters';

  if (!key) return 'private key is required';

  // SEED-PHRASE CHECK FIRST, before the hex/length rules.
  //
  // Order matters and a test caught me getting it wrong: a 12-word phrase is neither 64
  // chars nor hex, so the length check below fired first and returned "must be 64 hex
  // characters, got 92" — technically true, actively unhelpful, and it would not tell
  // someone they had just pasted their recovery phrase into a web form. Pasting a seed
  // phrase is a plausible mistake with an unpleasant outcome, so it gets its own message.
  if (key.split(/\s+/).length >= 12) {
    return 'that looks like a seed phrase, not a private key — never paste a seed phrase';
  }

  const bare = key.startsWith('0x') ? key.slice(2) : key;
  if (bare.length !== 64) {
    return `private key must be 64 hex characters (optionally 0x-prefixed), got ${bare.length}`;
  }
  if (!/^[0-9a-fA-F]{64}$/.test(bare)) return 'private key contains non-hex characters';

  return null;
}

/**
 * The wallet tab's HTML.
 *
 * Rendered as a string to match how every other tab in `tradepanel.ts` renders; the caller
 * wires listeners after inserting it.
 */
export function renderWallet(state: CredentialState | null): string {
  const s = state ?? { configured: false, address: null };
  const armed = s.trading_enabled === true;

  // WHAT THE USER NEEDS TO KNOW, AND NOTHING ELSE.
  //
  // Andrei's instruction, and he is right: "we don't need to display technical information
  // on the page." The earlier version leaked implementation detail into the UI — parameter
  // stores, environment variables, subprocess boundaries. None of that helps someone decide
  // what to type, and a page that explains its own plumbing is a page that has stopped
  // being a product. Diagnostics belong in logs, which an operator account can read.
  //
  // The two facts that DO belong here are the ones with consequences: which wallet is
  // wired up, and whether the app can place orders. Those change what the user does next.
  const stateLine = s.configured
    ? `<div id="creds-state">Connected to <b>${escapeHtml(shortAddress(s.address))}</b>` +
      `${s.testnet ? ' on <b>testnet</b>' : ''} — ` +
      `<b>${armed ? 'trading enabled' : 'view only'}</b>.</div>`
    : `<div id="creds-state">Connect a wallet to see your positions, orders and balances.</div>`;

  return `
    <div id="creds">
      ${stateLine}
      <div class="row">
        <div class="field">
          <label for="creds-address">Account address — your main wallet, not the API wallet</label>
          <input id="creds-address" type="text" spellcheck="false" autocapitalize="off"
                 autocomplete="off" placeholder="0x…"
                 value="${escapeHtml(s.address ?? '')}" />
          <div class="hint">
            Positions, orders and trades are read from THIS address. An API (agent) wallet owns
            nothing, so pasting its address here shows an empty account. The key below may be an
            agent key — it only signs orders.
          </div>
        </div>
      </div>
      <div class="row">
        <div class="field">
          <label for="creds-key">Private key ${s.key_present ? '(enter a new one to replace)' : ''}</label>
          <input id="creds-key" type="password" spellcheck="false" autocapitalize="off"
                 autocomplete="new-password" placeholder="Paste your key" />
        </div>
      </div>
      <div class="row">
        <label class="checkline">
          <input id="creds-testnet" type="checkbox" ${s.testnet ? 'checked' : ''} />
          Use testnet
        </label>
        <button id="creds-save" type="button">${s.configured ? 'Update' : 'Connect'} wallet</button>
        ${s.configured ? '<button id="creds-delete" type="button">Disconnect</button>' : ''}
      </div>
      <div id="creds-msg"></div>
      ${slMonitorBlock(s)}
      <div id="creds-warn">
        Your private key can <b>withdraw funds</b>, not just trade. Consider using a
        Hyperliquid <b>agent wallet</b> — it can place orders but cannot withdraw, so a
        mistake costs a bad trade rather than your balance. The key is encrypted, never
        shown again, and you can disconnect at any time.
      </div>
    </div>`;
}

/**
 * The stop-loss monitoring opt-in. RENDERED ONLY WHEN A WALLET IS CONNECTED.
 *
 * Not a style choice: the subscription lives on the wallet row and the server refuses to set it
 * without an address (`attribute_exists(address)`), so showing the checkbox first would offer a
 * control whose only outcome is an error. Connect, then subscribe.
 *
 * IT SAYS WHAT ARRIVES AND WHERE, because "monitoring" alone does not tell anyone what they signed
 * up for — one message a morning, only when a stop can move, to their own Telegram if they have set
 * one up in the Alerts tab. NO mention of Step Functions, DynamoDB attributes or a cron expression:
 * Andrei's standing rule is that the page does not explain its own plumbing.
 */
function slMonitorBlock(s: CredentialState): string {
  if (!s.configured) return '';
  const on = s.sl_monitor_enabled === true;
  return `
      <div class="row">
        <label class="checkline">
          <input id="creds-slmon" type="checkbox" ${on ? 'checked' : ''} />
          Watch my stops and message me each morning
        </label>
        <div class="hint">
          One message a day, only when a stop can move — never an order. It goes to your own
          Telegram if you have set one up under Alerts.
        </div>
      </div>
      <div id="creds-slmon-msg"></div>`;
}

/** `0x1234…abcd` — enough to recognise a wallet, short enough to read at a glance. */
function shortAddress(addr: string | null): string {
  const a = (addr ?? '').trim();
  if (a.length <= 12) return a;
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

/** Minimal HTML escape for the address echoed back into a value attribute. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
